import { createAction } from '@reduxjs/toolkit';
import { SliceKey, SliceReducer } from './types';

type Params = {
  sliceReducer: SliceReducer;
  sliceKey?: SliceKey;
  oneLevelNestedPathKeys?: [string, SliceKey];
};

/**
 * Creates a Redux action to clear specific fields (status or error) from a reducer's state OR the entire state of a reducer.
 * This action is designed to work with state structures that have up to two levels of nesting.
 *
 * @example
 * ```javascript
 * Example state structure:
 * {
 *   state: {
 *     error: 'errorLevel1',
 *     status: 'statusLevel1'
 *     data1: {
 *       error: 'errorLevel2',
 *       status: 'statusLevel2',
 *       deepData: {
 *         error: 'errorLevel3'
 *         status: 'statusLevel3',
 *       }
 *     },
 *     data2: {
 *       error: 'errorLevel2',
 *       status: 'statusLevel2',
 *     },
 *   }
 * }
 * ```
 * It can clear `state.error`, `state.status`, `state.data1.error`, `state.data1.status`, `state.data2.error`, `state.data2.status` or `the entire state`.
 *
 * @param {Params} params - Parameters for the action creation.
 * @param {SliceReducer} params.sliceReducer - The slice reducer that contains the state to be cleared.
 * @param {SliceKey | undefined} params.sliceKey - The key of the state to be cleared from the slice reducer (ex: `error`). Can be undefined.
 * @param {[string, SliceKey] | undefined} params.oneLevelNestedPathKeys - refer to the keys of the initial level within a nested state, indicating the paths to be excluded when clearing the slice reducer (ex: `['saveCommunication', 'error']`). Can be undefined.
 * @returns An action object with a payload containing the slice reducer, key of the state to clear (or undefined), and excluded paths (or undefined).
 * @example
 * ```javascript
 * Example return with sliceKey:
 * {
 *   type: 'CLEAR_STATE_SLICE',
 *   payload: {
 *     sliceReducer: 'SendCommunication',
 *     sliceKey: 'error',
 *   }
 * }
 * ```
 * @example
 * ```javascript
 * Example return with oneLevelNestedPathKeys:
 * {
 *   type: 'CLEAR_STATE_SLICE',
 *   payload: {
 *     sliceReducer: 'SendCommunication',
 *     oneLevelNestedPathKeys: ['saveCommunication', 'error']
 *   }
 * }
 * ```
 */
export const clearStateSlice = createAction(
  'CLEAR_STATE_SLICE',
  ({ sliceReducer, sliceKey, oneLevelNestedPathKeys }: Params) => ({
    payload: { sliceReducer, sliceKey, oneLevelNestedPathKeys },
  })
);

export default {
  clearStateSlice,
};
